import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import LogoMark from "../components/LogoMark";
import { api, formatApiError } from "../lib/api";
import { KeyRound, CheckCircle2 } from "lucide-react";

/**
 * Self-service password reset. Two steps on one page:
 *   1. Enter email → backend sends 6-digit OTP
 *   2. Enter OTP + new password + confirm → password updated
 * Both endpoints are rate-limited server-side; response never confirms
 * whether the email exists (security best practice) but preview builds
 * echo the OTP in `dev_otp` for testing.
 */
export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // 'email' | 'otp' | 'done'
  const [email, setEmail] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [devOtp, setDevOtp] = useState(null);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setSessionId(data.session_id);
      setDevOtp(data.dev_otp || null);
      setEmailEnabled(!!data.email_enabled);
      setStep("otp");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", {
        session_id: sessionId,
        otp: otp.trim(),
        password,
        confirm_password: confirmPassword,
      });
      setStep("done");
      setTimeout(() => navigate("/login", { replace: true }), 1600);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-[#050d1a] relative">
      <div className="csc-grid-bg absolute inset-0 opacity-30" />
      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-8"><LogoMark size={48} withText /></div>

        {step === "email" && (
          <form onSubmit={requestOtp} className="csc-card p-8 space-y-5" data-testid="forgot-email-form">
            <div className="flex items-center gap-3">
              <KeyRound size={22} className="text-[#00aaff]" />
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#00aaff]">Reset Password</div>
                <h1 className="text-2xl font-semibold mt-1">Forgot your password?</h1>
              </div>
            </div>
            <p className="text-sm text-[#94a3b8]">
              Enter the email tied to your account. We&apos;ll send you a 6-digit code to reset your password.
            </p>
            <div>
              <label className="csc-label">Email</label>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="csc-input"
                data-testid="forgot-email-input"
                autoFocus
              />
            </div>
            {error && <div className="text-sm text-red-400" data-testid="forgot-email-error">{error}</div>}
            <button disabled={submitting} className="csc-btn-primary w-full" data-testid="forgot-email-submit">
              {submitting ? "Sending…" : "Send reset code"}
            </button>
            <p className="text-center text-xs font-mono-tech text-[#94a3b8]">
              <Link to="/login" className="csc-link" data-testid="forgot-back-to-login">← Back to sign in</Link>
            </p>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={submitReset} className="csc-card p-8 space-y-5" data-testid="forgot-otp-form">
            <div className="flex items-center gap-3">
              <KeyRound size={22} className="text-[#39ff14]" />
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">Verify & Reset</div>
                <h1 className="text-2xl font-semibold mt-1">Check your inbox</h1>
              </div>
            </div>
            <p className="text-sm text-[#94a3b8]">
              We sent a 6-digit code to <span className="text-white font-mono-tech">{email}</span>. Enter it below and choose a new password.
            </p>
            {devOtp && (
              <div className="csc-card !p-3 !border-[#f59e0b]/60 text-xs font-mono-tech text-[#f59e0b]" data-testid="forgot-dev-otp">
                ⚠ PREVIEW / DEV ENV — no email was sent. Test-only OTP:
                <span className="text-white ml-2 text-sm">{devOtp}</span>
              </div>
            )}
            {!devOtp && !emailEnabled && (
              <div className="csc-card !p-3 !border-red-400/60 text-xs text-red-300" data-testid="forgot-smtp-warning">
                ⚠ Email delivery is currently disabled on this server. Please contact the site administrator to receive your reset code, or try again after SMTP is configured.
              </div>
            )}
            <div>
              <label className="csc-label">6-digit code</label>
              <input
                required
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                className="csc-input text-center text-2xl tracking-[0.5em] font-mono-tech"
                placeholder="••••••"
                autoFocus
                data-testid="forgot-otp-input"
              />
            </div>
            <div>
              <label className="csc-label">New password</label>
              <input
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="csc-input"
                data-testid="forgot-new-password"
                minLength={8}
              />
              <div className="text-[10px] font-mono-tech text-[#94a3b8] mt-1">At least 8 characters, mix of letters, numbers &amp; symbols recommended.</div>
            </div>
            <div>
              <label className="csc-label">Confirm new password</label>
              <input
                required
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="csc-input"
                data-testid="forgot-confirm-password"
                minLength={8}
              />
            </div>
            {error && <div className="text-sm text-red-400" data-testid="forgot-otp-error">{error}</div>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setStep("email"); setOtp(""); setPassword(""); setConfirmPassword(""); setError(""); }}
                className="csc-btn-ghost"
                data-testid="forgot-otp-back"
              >
                ← Back
              </button>
              <button
                disabled={submitting || otp.length !== 6 || password.length < 8}
                className="csc-btn-primary flex-1"
                data-testid="forgot-otp-submit"
              >
                {submitting ? "Resetting…" : "Reset password"}
              </button>
            </div>
          </form>
        )}

        {step === "done" && (
          <div className="csc-card p-8 space-y-5 text-center" data-testid="forgot-done">
            <CheckCircle2 size={48} className="mx-auto text-[#39ff14]" />
            <h1 className="text-2xl font-semibold">Password updated</h1>
            <p className="text-sm text-[#94a3b8]">You can now sign in with your new password. Redirecting…</p>
            <Link to="/login" className="csc-btn-primary w-full block">Sign in now</Link>
          </div>
        )}
      </div>
    </div>
  );
}
